import { type FC, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { MimeType } from '@repo/shared';
import { toast } from 'sonner';

import { useCreateDataset } from '@/api/generated';
import type { CreateDatasetRequest } from '@/api/generated.schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileInput } from '@/components/ui/file-input';
import FileUpload from '@/components/ui/file-upload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  type CreateDatasetSchema,
  createDatasetSchema,
} from '@/routes/(app)/(datasets)/lib/schema';

interface CreateDatasetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refetch: () => void;
}

const CreateDatasetDialog: FC<CreateDatasetDialogProps> = ({
  open,
  onOpenChange,
  refetch,
}) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useCreateDataset();

  const form = useForm<CreateDatasetSchema>({
    resolver: zodResolver(createDatasetSchema),
    defaultValues: {
      files: [],
    },
  });
  const filesWatch = form.watch('files');

  const handleSubmit = useCallback(
    (values: CreateDatasetSchema) => {
      const formData = new FormData();
      // looks wrong, but it is correct.
      values.files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append(
        'data',
        JSON.stringify({
          name: values.name,
        } as CreateDatasetSchema)
      );

      mutate(
        { data: formData as unknown as CreateDatasetRequest },
        {
          onSuccess: () => {
            toast.success(t('datasets.createDataset.success'));
            onOpenChange(false);
            form.reset();
          },
          onError: () => {
            toast.error(t('datasets.createDataset.error'));
          },
          onSettled: () => {
            refetch();
          },
        }
      );
    },
    [form, mutate, onOpenChange, refetch, t]
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    form.reset();
  }, [form, onOpenChange]);

  const handleRemoveFile = useCallback(
    (file: File) => {
      form.setValue(
        'files',
        form.getValues().files.filter((_file) => _file !== file)
      );
    },
    [form]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('datasets.createDataset.title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('datasets.createDataset.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='files'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('datasets.createDataset.file')}</FormLabel>
                  <FormControl>
                    <FileInput
                      {...field}
                      mimeTypes={[
                        MimeType.PDF,
                        MimeType.DOC,
                        MimeType.DOCX,
                        MimeType.MARKDOWN,
                      ]}
                      multiple
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {filesWatch.map((file) => (
              <FileUpload
                key={file.name}
                file={file}
                onRemove={handleRemoveFile}
              />
            ))}

            <DialogFooter>
              <Button
                variant='outline'
                onClick={handleCancel}>
                {t('datasets.createDataset.cancel')}
              </Button>
              <Button
                type='submit'
                disabled={isPending}>
                {t('datasets.createDataset.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDatasetDialog;
